import { useEffect, useMemo, useRef, useState } from 'react'
import type { DrinkEntry } from '@/types'
import type { DailyAggregate, Metric } from '@/utils/analytics'
import { METRIC_SPECS, getBucketLevel, getMetricValue } from '@/utils/analytics'

interface ContributionHeatmapProps {
  aggregates: DailyAggregate[]
  entries: DrinkEntry[]
  metric: Metric
}

const CELL = 13
const GAP = 3
const ROW_LABEL_WIDTH = 26
const MONTH_LABEL_HEIGHT = 14
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const EMPTY_COLOR = 'rgba(148, 163, 184, 0.10)'
const POPOVER_WIDTH = 260

function startOfWeek(d: Date): Date {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const day = (out.getDay() + 6) % 7
  out.setDate(out.getDate() - day)
  return out
}

function dateKey(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${dd}`
}

function formatLong(d: Date): string {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  return `${days[d.getDay()]} ${d.getDate()} ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`
}

function buildTooltip(date: Date, agg: DailyAggregate | undefined, dateStr: string): string {
  if (!agg) return `${dateStr}\nNo drinks logged`
  const lines = [
    dateStr,
    `Peak BAC: ${agg.peakBac.toFixed(3)}%`,
    `Drinks: ${agg.drinks} (${agg.stdDrinks.toFixed(1)} std)`,
    `Volume: ${Math.round(agg.ml)} mL`,
    `Calories: ${Math.round(agg.calories)} kcal`,
    'Click for details',
  ]
  if (date.getTime() > Date.now()) lines.push('(future)')
  return lines.join('\n')
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
}

function fmtDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`
  const h = Math.floor(seconds / 3600)
  const m = Math.round((seconds % 3600) / 60)
  if (h === 0) return `${m}m`
  return `${h}h ${m}m`
}

interface Cell {
  date: Date
  key: string
  aggregate: DailyAggregate | undefined
}

interface ActiveCell {
  rect: DOMRect
  date: Date
  agg: DailyAggregate | undefined
  drinks: DrinkEntry[]
}

export default function ContributionHeatmap({ aggregates, entries, metric }: ContributionHeatmapProps) {
  const spec = METRIC_SPECS[metric]
  const scrollerRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState<ActiveCell | null>(null)

  // Per-day entry lookup (only non-gap entries — gaps stay invisible here)
  const entriesByDate = useMemo(() => {
    const map = new Map<string, DrinkEntry[]>()
    for (const e of entries) {
      if (e.is_gap) continue
      const k = dateKey(new Date(e.finished_at))
      let arr = map.get(k)
      if (!arr) { arr = []; map.set(k, arr) }
      arr.push(e)
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => new Date(a.finished_at).getTime() - new Date(b.finished_at).getTime())
    }
    return map
  }, [entries])

  const { weeks, monthLabels } = useMemo(() => {
    if (aggregates.length === 0) {
      return { weeks: [] as Cell[][], monthLabels: [] as { col: number; label: string }[] }
    }
    const byKey = new Map(aggregates.map(a => [a.date, a]))
    const earliest = new Date(aggregates[0].date + 'T00:00:00')
    const today = new Date()
    const start = startOfWeek(earliest)
    const endWeekStart = startOfWeek(today)

    const weeks: Cell[][] = []
    const monthLabels: { col: number; label: string }[] = []
    let prevMonth = -1
    let col = 0
    const cursor = new Date(start)

    while (cursor.getTime() <= endWeekStart.getTime()) {
      const week: Cell[] = []
      for (let i = 0; i < 7; i++) {
        const d = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + i)
        const key = dateKey(d)
        week.push({ date: d, key, aggregate: byKey.get(key) })
      }
      weeks.push(week)
      const firstOfWeek = week[0].date
      if (firstOfWeek.getMonth() !== prevMonth) {
        monthLabels.push({ col, label: MONTH_NAMES[firstOfWeek.getMonth()] })
        prevMonth = firstOfWeek.getMonth()
      }
      col += 1
      cursor.setDate(cursor.getDate() + 7)
    }

    return { weeks, monthLabels }
  }, [aggregates])

  // Scroll to the right on mount / when data changes so latest week is visible.
  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    el.scrollLeft = el.scrollWidth
  }, [weeks.length])

  // Close the popover on outside click. Deferred so the same click that opened
  // it doesn't immediately close it.
  useEffect(() => {
    if (!active) return
    const handler = (e: MouseEvent) => {
      if (popoverRef.current?.contains(e.target as Node)) return
      setActive(null)
    }
    const id = window.setTimeout(() => {
      document.addEventListener('mousedown', handler)
    }, 0)
    return () => {
      window.clearTimeout(id)
      document.removeEventListener('mousedown', handler)
    }
  }, [active])

  // Close on scroll (popover position would otherwise drift away from the cell).
  useEffect(() => {
    if (!active) return
    const close = () => setActive(null)
    const el = scrollerRef.current
    el?.addEventListener('scroll', close, { passive: true })
    window.addEventListener('scroll', close, { passive: true })
    window.addEventListener('resize', close)
    return () => {
      el?.removeEventListener('scroll', close)
      window.removeEventListener('scroll', close)
      window.removeEventListener('resize', close)
    }
  }, [active])

  // Close on Escape
  useEffect(() => {
    if (!active) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setActive(null) }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [active])

  if (weeks.length === 0) {
    return (
      <div className="card text-center text-sm text-[var(--text-secondary)]">
        Log a drink to start seeing your daily pattern here.
      </div>
    )
  }

  const colWidth = CELL + GAP
  const gridWidth = weeks.length * colWidth - GAP
  const gridHeight = 7 * colWidth - GAP
  const totalHeight = MONTH_LABEL_HEIGHT + gridHeight

  const handleCellClick = (e: React.MouseEvent<SVGRectElement>, cell: Cell) => {
    e.stopPropagation()
    const rect = (e.currentTarget as SVGRectElement).getBoundingClientRect()
    setActive(prev => {
      if (prev && prev.date.getTime() === cell.date.getTime()) return null
      return {
        rect,
        date: cell.date,
        agg: cell.aggregate,
        drinks: entriesByDate.get(cell.key) ?? [],
      }
    })
  }

  return (
    <div className="card">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm font-semibold text-[var(--text-primary)]">Daily {spec.label}</h2>
        <p className="text-xs text-[var(--text-secondary)]">Tap a day for details</p>
      </div>

      <div className="flex gap-2">
        <div
          className="shrink-0 relative text-[10px] text-[var(--text-secondary)] select-none"
          style={{ width: ROW_LABEL_WIDTH, height: totalHeight }}
        >
          {DAY_LABELS.map((label, i) => {
            if (i !== 0 && i !== 2 && i !== 4) return null
            return (
              <span
                key={label}
                className="absolute right-1"
                style={{ top: MONTH_LABEL_HEIGHT + i * colWidth - 1 }}
              >
                {label}
              </span>
            )
          })}
        </div>

        <div
          ref={scrollerRef}
          className="overflow-x-auto flex-1 min-w-0"
          style={{ scrollbarWidth: 'thin' }}
        >
          <div style={{ width: gridWidth, height: totalHeight, position: 'relative' }}>
            <div
              className="text-[10px] text-[var(--text-secondary)] select-none"
              style={{ position: 'relative', height: MONTH_LABEL_HEIGHT }}
            >
              {monthLabels.map(({ col, label }, i) => {
                if (i > 0 && col - monthLabels[i - 1].col < 3) return null
                return (
                  <span
                    key={`${col}-${label}`}
                    className="absolute"
                    style={{ left: col * colWidth }}
                  >
                    {label}
                  </span>
                )
              })}
            </div>

            <svg
              width={gridWidth}
              height={gridHeight}
              style={{ display: 'block' }}
              role="img"
              aria-label={`Daily ${spec.label} heatmap`}
            >
              {weeks.map((week, wIdx) =>
                week.map((cell, dIdx) => {
                  const value = cell.aggregate ? getMetricValue(cell.aggregate, metric) : 0
                  const level = getBucketLevel(value, spec.buckets)
                  const color = level === 0 ? EMPTY_COLOR : spec.colors[level - 1]
                  const isFuture = cell.date.getTime() > Date.now()
                  if (isFuture) return null
                  const tooltip = buildTooltip(cell.date, cell.aggregate, formatLong(cell.date))
                  const isActive = active?.date.getTime() === cell.date.getTime()
                  return (
                    <rect
                      key={cell.key}
                      x={wIdx * colWidth}
                      y={dIdx * colWidth}
                      width={CELL}
                      height={CELL}
                      rx={2}
                      ry={2}
                      fill={color}
                      stroke={isActive ? 'var(--accent-teal)' : undefined}
                      strokeWidth={isActive ? 1.5 : 0}
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => handleCellClick(e, cell)}
                    >
                      <title>{tooltip}</title>
                    </rect>
                  )
                })
              )}
            </svg>
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-end gap-x-3 gap-y-1.5 text-[10px] text-[var(--text-secondary)]">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block rounded-sm" style={{ width: CELL, height: CELL, backgroundColor: EMPTY_COLOR }} />
          No drinks
        </span>
        {spec.colors.map((c, i) => (
          <span key={i} className="inline-flex items-center gap-1.5">
            <span className="inline-block rounded-sm" style={{ width: CELL, height: CELL, backgroundColor: c }} />
            {spec.bucketLabels[i]}
          </span>
        ))}
      </div>

      {active && <DayDetailPopover popoverRef={popoverRef} active={active} onClose={() => setActive(null)} />}
    </div>
  )
}

interface DayDetailPopoverProps {
  active: ActiveCell
  popoverRef: React.MutableRefObject<HTMLDivElement | null>
  onClose: () => void
}

function DayDetailPopover({ active, popoverRef, onClose }: DayDetailPopoverProps) {
  const { rect, date, agg, drinks } = active

  // Position above the cell if there's room; otherwise below.
  const placeBelow = rect.top < 240
  const left = Math.min(
    Math.max(12, rect.left + rect.width / 2 - POPOVER_WIDTH / 2),
    window.innerWidth - POPOVER_WIDTH - 12
  )
  const style: React.CSSProperties = {
    position: 'fixed',
    left,
    width: POPOVER_WIDTH,
    zIndex: 50,
    ...(placeBelow
      ? { top: rect.bottom + 8 }
      : { top: rect.top - 8, transform: 'translateY(-100%)' }),
  }

  // Group drinks by name + count
  const grouped = drinks.reduce<{ name: string; count: number; ml: number; stdDrinks: number }[]>((out, e) => {
    const ml = Number(e.drink.milliliters) || 0
    const abv = Number(e.drink.alcohol) || 0
    const std = (ml * (abv / 100) * 0.789) / 10
    const existing = out.find(x => x.name === e.drink.drink)
    if (existing) {
      existing.count += 1
      existing.ml += ml
      existing.stdDrinks += std
    } else {
      out.push({ name: e.drink.drink, count: 1, ml, stdDrinks: std })
    }
    return out
  }, [])

  let drinkingWindow: { start: string; end: string; durationSec: number } | null = null
  if (drinks.length >= 1) {
    const first = drinks[0].finished_at
    const last = drinks[drinks.length - 1].finished_at
    drinkingWindow = {
      start: fmtTime(first),
      end: fmtTime(last),
      durationSec: Math.max(0, (new Date(last).getTime() - new Date(first).getTime()) / 1000),
    }
  }

  return (
    <div
      ref={popoverRef}
      style={style}
      role="dialog"
      aria-label={`Details for ${formatLong(date)}`}
      className="bg-[var(--bg-secondary)] border border-[var(--border)] rounded-lg shadow-xl p-3 text-xs"
    >
      <div className="flex items-start justify-between mb-2">
        <p className="text-sm font-semibold text-[var(--text-primary)]">{formatLong(date)}</p>
        <button
          onClick={onClose}
          aria-label="Close"
          className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] -mt-1 -mr-1 p-1 cursor-pointer"
        >
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {!agg || drinks.length === 0 ? (
        <p className="text-[var(--text-secondary)]">No drinks logged.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[var(--text-secondary)] mb-2">
            <span>Drinks</span>
            <span className="text-right text-[var(--text-primary)]">{agg.drinks} ({agg.stdDrinks.toFixed(1)} std)</span>
            <span>Peak BAC</span>
            <span className="text-right text-[var(--text-primary)] font-mono">{agg.peakBac.toFixed(3)}%</span>
            <span>Volume</span>
            <span className="text-right text-[var(--text-primary)]">{Math.round(agg.ml).toLocaleString()} mL</span>
            <span>Calories</span>
            <span className="text-right text-[var(--text-primary)]">{Math.round(agg.calories).toLocaleString()} kcal</span>
            {drinkingWindow && (
              <>
                <span>Drinking window</span>
                <span className="text-right text-[var(--text-primary)]">
                  {drinkingWindow.start} → {drinkingWindow.end}
                </span>
                <span>Duration</span>
                <span className="text-right text-[var(--text-primary)]">
                  {drinkingWindow.durationSec > 0 ? fmtDuration(drinkingWindow.durationSec) : '—'}
                </span>
              </>
            )}
          </div>

          <div className="border-t border-[var(--border)] pt-2">
            <p className="text-[10px] uppercase tracking-wide text-[var(--text-secondary)] mb-1">Drinks logged</p>
            <ul className="space-y-0.5 max-h-32 overflow-y-auto">
              {grouped.map((g, i) => (
                <li key={i} className="flex justify-between text-[var(--text-primary)]">
                  <span className="truncate pr-2">
                    {g.count > 1 ? `${g.count}× ` : ''}{g.name}
                  </span>
                  <span className="text-[var(--text-secondary)] shrink-0 font-mono">
                    {g.stdDrinks.toFixed(1)} std
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  )
}
